import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Field, PasswordInput } from "@/components/base/FormControls";
import Alert from "@/components/base/Alert";
import { BRAND } from "@/config/brand";
import { supabase, getSafeSession } from "@/store/shared/supabase";

/**
 * Définition du mot de passe — page réelle cible du lien sécurisé Supabase Auth.
 *
 * Utilisé pour :
 *  - l'accès initial des comptes créés depuis le Super Admin (invitation) ;
 *  - la récupération de mot de passe classique (« Mot de passe oublié ? »).
 *
 * Aucun jeton n'est généré, affiché ni stocké par l'application : Supabase Auth
 * fournit le lien et gère le jeton. La session de récupération est détectée par
 * le SDK (detectSessionInUrl) puis on applique le nouveau mot de passe via
 * `auth.updateUser`.
 */

type Etat = "verification" | "pret" | "invalide" | "termine";

export default function ResetPasswordPage() {
  const [etat, setEtat] = useState<Etat>("verification");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;

    const evaluer = async () => {
      try {
        const storedSession = await getSafeSession();
        if (!active) return;
        setEmail(storedSession?.user?.email ?? "");
        setEtat((prev) => (prev === "termine" ? prev : storedSession ? "pret" : "invalide"));
      } catch {
        if (active) setEtat((prev) => (prev === "termine" ? prev : "invalide"));
      }
    };

    void evaluer();

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (!active) return;
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setEtat("pret");
    });

    // Laisse à supabase-js le temps de traiter le jeton présent dans l'URL
    // (fragment #access_token=… pour le flux implicite).
    const timer = window.setTimeout(() => {
      void evaluer();
    }, 700);

    return () => {
      active = false;
      window.clearTimeout(timer);
      sub.subscription.unsubscribe();
    };
  }, []);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const next: { password?: string; confirm?: string } = {};
    if (password.length < 8) next.password = "Le mot de passe doit contenir au moins 8 caractères.";
    if (confirm !== password) next.confirm = "Les deux mots de passe ne correspondent pas.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSaving(true);
    setFormError("");
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setFormError(
          "La définition du mot de passe a échoué. Le lien est peut-être expiré : demandez-en un nouveau."
        );
        return;
      }
      // Activation réelle de la fiche employé (si le compte correspond à un
      // employé invité) : la RPC est un no-op pour les autres comptes.
      try {
        await supabase.rpc("employe_activer_soi");
      } catch {
        /* activation best-effort : non bloquant pour la définition du mot de passe */
      }
      setEtat("termine");
      // On ferme la session de récupération : le titulaire se reconnecte avec
      // son nouveau mot de passe, ce qui confirme qu'il fonctionne réellement.
      await supabase.auth.signOut();
    } catch {
      setFormError("La définition du mot de passe a échoué. Réessayez.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-104px)] max-w-md flex-col justify-center px-4 py-14 md:px-8">
      <Link to="/" className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 font-heading text-sm font-bold text-background-50">
          {BRAND.initials}
        </span>
        <span className="font-heading text-base font-semibold text-foreground-950">{BRAND.name}</span>
      </Link>

      <h1 className="mt-8 text-2xl font-heading font-semibold text-foreground-950 md:text-3xl">
        Définir votre mot de passe
      </h1>

      {etat === "verification" && (
        <p className="mt-3 flex items-center gap-2 text-sm text-foreground-600">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-foreground-300 border-t-foreground-600" />
          Vérification du lien sécurisé…
        </p>
      )}

      {etat === "invalide" && (
        <div className="mt-6 flex flex-col gap-4">
          <Alert tone="error" title="Lien invalide ou expiré">
            Ce lien de définition du mot de passe n'est plus valide. Pour des raisons de sécurité, il est à usage
            unique et limité dans le temps.
          </Alert>
          <Link
            to="/connexion"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base" aria-hidden="true" />
            Aller à la connexion
          </Link>
        </div>
      )}

      {etat === "pret" && (
        <>
          <p className="mt-2 text-sm text-foreground-600">
            Choisissez votre mot de passe personnel pour accéder à votre espace ImmoBF. Ne partagez jamais ce lien.
          </p>

          {email && (
            <div className="mt-4 flex items-center gap-2.5 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5">
              <i className="ri-mail-line text-base text-foreground-500" aria-hidden="true" />
              <span className="truncate text-sm text-foreground-800">{email}</span>
            </div>
          )}

          {formError && (
            <Alert tone="error" className="mt-5" title="Action impossible">
              {formError}
            </Alert>
          )}

          <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            <Field label="Nouveau mot de passe" htmlFor="reset-password" error={errors.password} required>
              <PasswordInput
                id="reset-password"
                name="new-password"
                autoComplete="new-password"
                placeholder="Au moins 8 caractères"
                value={password}
                invalid={Boolean(errors.password)}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, password: "" }));
                }}
              />
            </Field>

            <Field label="Confirmer le mot de passe" htmlFor="reset-password-confirm" error={errors.confirm} required>
              <PasswordInput
                id="reset-password-confirm"
                name="confirm-password"
                autoComplete="new-password"
                placeholder="Retapez le mot de passe"
                value={confirm}
                invalid={Boolean(errors.confirm)}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  setErrors((prev) => ({ ...prev, confirm: "" }));
                }}
              />
            </Field>

            <button
              type="submit"
              disabled={saving}
              className="mt-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 hover:bg-primary-600 hover:-translate-y-0.5 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-50/40 border-t-background-50" />
              )}
              {saving ? "Enregistrement…" : "Définir mon mot de passe"}
            </button>
          </form>
        </>
      )}

      {etat === "termine" && (
        <div className="mt-6 flex flex-col gap-4">
          <Alert tone="success" title="Mot de passe défini">
            Votre mot de passe a bien été enregistré. Vous pouvez maintenant vous connecter à votre espace avec cette
            adresse e-mail et votre nouveau mot de passe.
          </Alert>
          <Link
            to="/connexion"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-login-box-line text-base" aria-hidden="true" />
            Se connecter
          </Link>
        </div>
      )}

      <p className="mt-6 text-xs leading-relaxed text-foreground-500">
        Pour votre sécurité, le lien de définition du mot de passe est à usage unique et ne doit jamais être partagé.
        Besoin d'aide ?{" "}
        <a href={`mailto:${BRAND.email}`} className="font-label font-medium text-primary-700 hover:text-primary-800">
          {BRAND.email}
        </a>
      </p>
    </div>
  );
}